import { Header } from '@/components/header'
import { Footer } from '@/components/footer'
import { useState, FormEvent, useEffect, useRef } from 'react'
import { AssociationsAndSuppliers } from '@/components/AssociationsAndSuppliers'
import { Helmet } from 'react-helmet-async'
import { ToastContainer, toast } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'

export default function Estimate() {
  // State for form values
  const [squareFootage, setSquareFootage] = useState('')
  const [deckMaterial, setDeckMaterial] = useState('')
  
  // State for conditional display fields
  const [hasRailing, setHasRailing] = useState('No')
  const [railingFeet, setRailingFeet] = useState('')
  const [railingMaterial, setRailingMaterial] = useState('')
  
  const [hasPrivacyWall, setHasPrivacyWall] = useState('No')
  const [privacyWallFeet, setPrivacyWallFeet] = useState('')
  
  const [hasStairs, setHasStairs] = useState('No')
  const [stairsCount, setStairsCount] = useState('')
  const [stairsMaterial, setStairsMaterial] = useState('')
  
  const [hasSkirting, setHasSkirting] = useState('No')
  const [skirtingFeet, setSkirtingFeet] = useState('')
  const [skirtingMaterial, setSkirtingMaterial] = useState('')
  
  const [hasFence, setHasFence] = useState('No')
  const [fenceFeet, setFenceFeet] = useState('')
  const [fenceMaterial, setFenceMaterial] = useState('')
  
  const [hasGate, setHasGate] = useState('No')
  const [gateCount, setGateCount] = useState('')
  
  const [hasRVGate, setHasRVGate] = useState('No')
  
  // State for contact form
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [phone, setPhone] = useState('')
  const [email, setEmail] = useState('')
  
  // State for estimates
  const [estimatedPrice, setEstimatedPrice] = useState('$0.00')
  const [monthlyPayment, setMonthlyPayment] = useState('$0.00')
  
  // State for form submission
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState('')
  const [submitSuccess, setSubmitSuccess] = useState(false)
  // Toggle for form submission method
  const useFormSubmit = true // Set to false to use MailerSend
  const useMailerSend = !useFormSubmit
  
  // Reference to the form element
  const formRef = useRef<HTMLFormElement>(null)
  
  // Material pricing data
  const deckMaterialPrices = {
    'Vinyl decking': 38,
    'Pressure treated wood': 31,
    'Composite': 44.5,
  }
  
  const railingMaterialPrices = {
    'Glass Railing': 54.5,
    'Aluminum Picket': 44,
    'Pressure Treated Wood': 36,
  }
  
  const stairsMaterialPrices = {
    'Composite': 230,
    'Pressure Treated': 130,
  }
  
  const skirtingMaterialPrices = {
    'Lattice': 34,
    'Smart Board': 46,
    'Pressure Treated': 36,
  }
  
  const fenceMaterialPrices = {
    'Pressure Treated 4x4 Posts': 68,
    'Pressure Treated 4x6 Posts': 72,
    'Cedar 4x4 Posts': 80,
    'Cedar 4x6 Posts': 84,
  }
  
  // Handle form submission
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    
    // Using FormSubmit when toggle is set
    if (useFormSubmit) {
      try {
        setIsSubmitting(true)
        
        if (formRef.current) {
          // Create FormData from the form
          const formData = new FormData(formRef.current);
          
          // Add FormSubmit specific fields
          formData.append('_subject', 'New Deck Estimate Claim');
          formData.append('_template', 'table');
          formData.append('_captcha', 'false');
          formData.append('_cc', email);
          
          // Add all estimate data as fields
          formData.append('squareFootage', squareFootage);
          formData.append('deckMaterial', deckMaterial);
          formData.append('hasRailing', hasRailing);
          formData.append('railingFeet', railingFeet);
          formData.append('railingMaterial', railingMaterial);
          formData.append('hasPrivacyWall', hasPrivacyWall);
          formData.append('privacyWallFeet', privacyWallFeet);
          formData.append('hasStairs', hasStairs);
          formData.append('stairsCount', stairsCount);
          formData.append('stairsMaterial', stairsMaterial);
          formData.append('hasSkirting', hasSkirting);
          formData.append('skirtingFeet', skirtingFeet);
          formData.append('skirtingMaterial', skirtingMaterial);
          formData.append('hasFence', hasFence);
          formData.append('fenceFeet', fenceFeet);
          formData.append('fenceMaterial', fenceMaterial);
          formData.append('hasGate', hasGate);
          formData.append('gateCount', gateCount);
          formData.append('hasRVGate', hasRVGate);
          formData.append('estimatedPrice', estimatedPrice);
          formData.append('monthlyPayment', monthlyPayment);
          
          // Send data to FormSubmit endpoint
          const response = await fetch('https://formsubmit.co/ajax/sales@busybeaverconstruction.com', {
            method: 'POST',
            body: formData,
          });
          
          const data = await response.json();
          
          if (data.success === "true" || data.success === true) {
            setSubmitSuccess(true);
            toast.success("Estimate submitted successfully!");
          } else {
            setSubmitError('Error submitting estimate. Please try again.');
            toast.error("Error submitting estimate. Please try again.");
          }
        } else {
          setSubmitError('Error accessing form. Please try again.');
          toast.error("Error accessing form. Please try again.");
        }
      } catch (error) {
        setSubmitError('Error submitting estimate. Please try again.');
        toast.error("Error submitting estimate. Please try again.");
        console.error("FormSubmit error:", error);
      } finally {
        setIsSubmitting(false);
      }
      return;
    }
    
    // MailerSend functionality when toggle is set
    if (useMailerSend) {
      // Reset submission states
      setIsSubmitting(true)
      setSubmitError('')
      setSubmitSuccess(false)
      
      try {
        // Prepare the data for submission
        const formData = {
          firstName,
          lastName,
          phone,
          email,
          squareFootage,
          deckMaterial,
          hasRailing,
          railingFeet,
          railingMaterial,
          hasPrivacyWall,
          privacyWallFeet,
          hasStairs,
          stairsCount,
          stairsMaterial,
          hasSkirting,
          skirtingFeet,
          skirtingMaterial,
          hasFence,
          fenceFeet,
          fenceMaterial,
          hasGate,
          gateCount,
          hasRVGate,
          estimatedPrice,
          monthlyPayment
        }
        
        // Send the data to our API endpoint using fetch
        const response = await fetch('/api/send-estimate', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(formData),
        });
        
        const data = await response.json();
        
        if (data.success) {
          setSubmitSuccess(true)
          toast.success("Estimate submitted successfully!");
          // Reset form fields if needed
          // resetForm()
        } else {
          // Fall back to FormSubmit if MailerSend fails
          setSubmitError('Using alternative form submission...')
          toast.warning("Primary submission method failed. Using alternative...");
          
          if (formRef.current) {
            // Create FormData from the form
            const formData = new FormData(formRef.current);
            
            // Add FormSubmit specific fields
            formData.append('_subject', 'New Deck Estimate Claim');
            formData.append('_template', 'table');
            formData.append('_captcha', 'false');
            formData.append('_cc', email);
            
            // Add all estimate data as fields
            formData.append('squareFootage', squareFootage);
            formData.append('deckMaterial', deckMaterial);
            formData.append('hasRailing', hasRailing);
            formData.append('railingFeet', railingFeet);
            formData.append('railingMaterial', railingMaterial);
            formData.append('hasPrivacyWall', hasPrivacyWall);
            formData.append('privacyWallFeet', privacyWallFeet);
            formData.append('hasStairs', hasStairs);
            formData.append('stairsCount', stairsCount);
            formData.append('stairsMaterial', stairsMaterial);
            formData.append('hasSkirting', hasSkirting);
            formData.append('skirtingFeet', skirtingFeet);
            formData.append('skirtingMaterial', skirtingMaterial);
            formData.append('hasFence', hasFence);
            formData.append('fenceFeet', fenceFeet);
            formData.append('fenceMaterial', fenceMaterial);
            formData.append('hasGate', hasGate);
            formData.append('gateCount', gateCount);
            formData.append('hasRVGate', hasRVGate);
            formData.append('estimatedPrice', estimatedPrice);
            formData.append('monthlyPayment', monthlyPayment);
            
            // Send data to FormSubmit endpoint
            const formSubmitResponse = await fetch('https://formsubmit.co/ajax/sales@busybeaverconstruction.com', {
              method: 'POST',
              body: formData,
            });
            
            const formSubmitData = await formSubmitResponse.json();
            
            if (formSubmitData.success === "true" || formSubmitData.success === true) {
              setSubmitSuccess(true);
              toast.success("Estimate submitted successfully using alternative method!");
            } else {
              setSubmitError('Error submitting estimate. Please try again.');
              toast.error("Error submitting estimate. Please try contacting us directly.");
            }
          }
        }
      } catch (error) {
        console.error('Error submitting form:', error)
        // Fall back to FormSubmit if there's an error
        setSubmitError('Using alternative form submission...')
        toast.warning("Primary submission method failed. Using alternative...");
        
        try {
          if (formRef.current) {
            // Create FormData from the form
            const formData = new FormData(formRef.current);
            
            // Add FormSubmit specific fields
            formData.append('_subject', 'New Deck Estimate Claim');
            formData.append('_template', 'table');
            formData.append('_captcha', 'false');
            formData.append('_cc', email);
            
            // Add all estimate data as fields
            formData.append('squareFootage', squareFootage);
            formData.append('deckMaterial', deckMaterial);
            formData.append('hasRailing', hasRailing);
            formData.append('railingFeet', railingFeet);
            formData.append('railingMaterial', railingMaterial);
            formData.append('hasPrivacyWall', hasPrivacyWall);
            formData.append('privacyWallFeet', privacyWallFeet);
            formData.append('hasStairs', hasStairs);
            formData.append('stairsCount', stairsCount);
            formData.append('stairsMaterial', stairsMaterial);
            formData.append('hasSkirting', hasSkirting);
            formData.append('skirtingFeet', skirtingFeet);
            formData.append('skirtingMaterial', skirtingMaterial);
            formData.append('hasFence', hasFence);
            formData.append('fenceFeet', fenceFeet);
            formData.append('fenceMaterial', fenceMaterial);
            formData.append('hasGate', hasGate);
            formData.append('gateCount', gateCount);
            formData.append('hasRVGate', hasRVGate);
            formData.append('estimatedPrice', estimatedPrice);
            formData.append('monthlyPayment', monthlyPayment);
            
            // Send data to FormSubmit endpoint
            const formSubmitResponse = await fetch('https://formsubmit.co/ajax/sales@busybeaverconstruction.com', {
              method: 'POST',
              body: formData,
            });
            
            const formSubmitData = await formSubmitResponse.json();
            
            if (formSubmitData.success === "true" || formSubmitData.success === true) {
              setSubmitSuccess(true);
              toast.success("Estimate submitted successfully using alternative method!");
            } else {
              setSubmitError('Error submitting estimate. Please try again.');
              toast.error("Error submitting estimate. Please try contacting us directly.");
            }
          }
        } catch (formSubmitError) {
          setSubmitError('Error submitting estimate. Please try contacting us directly.');
          toast.error("Error submitting estimate. Please try contacting us directly.");
          console.error("FormSubmit error:", formSubmitError);
        }
      } finally {
        setIsSubmitting(false)
      }
    }
  }
  
  // Calculate estimated price whenever relevant inputs change
  useEffect(() => {
    updateEstimate()
  }, [
    squareFootage, deckMaterial,
    hasRailing, railingFeet, railingMaterial,
    hasPrivacyWall, privacyWallFeet,
    hasStairs, stairsCount, stairsMaterial,
    hasSkirting, skirtingFeet, skirtingMaterial,
    hasFence, fenceFeet, fenceMaterial,
    hasGate, gateCount,
    hasRVGate
  ])
  
  // Calculate price based on inputs
  const updateEstimate = () => {
    const sqFt = parseFloat(squareFootage) || 0
    const deckMaterialCost = deckMaterial ? deckMaterialPrices[deckMaterial as keyof typeof deckMaterialPrices] || 0 : 0
    
    const railingLft = hasRailing === 'Yes' ? parseFloat(railingFeet) || 0 : 0
    const railingMaterialCost = railingMaterial ? railingMaterialPrices[railingMaterial as keyof typeof railingMaterialPrices] || 0 : 0
    
    const privacyWallLft = hasPrivacyWall === 'Yes' ? parseFloat(privacyWallFeet) || 0 : 0
    const privacyWallCost = 135 // Fixed cost per linear foot
    
    const stairsQty = hasStairs === 'Yes' ? parseInt(stairsCount) || 0 : 0
    const stairsMaterialCost = stairsMaterial ? stairsMaterialPrices[stairsMaterial as keyof typeof stairsMaterialPrices] || 0 : 0
    
    const skirtingLft = hasSkirting === 'Yes' ? parseFloat(skirtingFeet) || 0 : 0
    const skirtingMaterialCost = skirtingMaterial ? skirtingMaterialPrices[skirtingMaterial as keyof typeof skirtingMaterialPrices] || 0 : 0
    
    const fenceLft = hasFence === 'Yes' ? parseFloat(fenceFeet) || 0 : 0
    const fenceMaterialCost = fenceMaterial ? fenceMaterialPrices[fenceMaterial as keyof typeof fenceMaterialPrices] || 0 : 0
    
    const gates = hasGate === 'Yes' ? parseInt(gateCount) || 0 : 0
    const gateCost = 625 // Fixed cost per gate
    
    const rvGateCost = hasRVGate === 'Yes' ? 950 : 0
    
    const totalCost = 
      (sqFt * deckMaterialCost) + 
      (railingLft * railingMaterialCost) + 
      (privacyWallLft * privacyWallCost) + 
      (stairsQty * stairsMaterialCost) + 
      (skirtingLft * skirtingMaterialCost) + 
      (fenceLft * fenceMaterialCost) + 
      (gates * gateCost) + 
      rvGateCost
    
    setEstimatedPrice(`$${totalCost.toFixed(2)}`)
    setMonthlyPayment(`$${(totalCost * 0.01351).toFixed(2)}`)
  }
  
  // Form element styling classes
  const selectClass = "w-full py-2 px-3 border border-gray-300 bg-white rounded-sm focus:outline-none focus:ring-1 focus:ring-beaver-green"
  const inputClass = "w-full py-2 px-3 border border-gray-300 bg-white rounded-sm focus:outline-none focus:ring-1 focus:ring-beaver-green"
  const labelClass = "block text-sm font-medium text-gray-700 mb-1"
  
  return (
    <main>
      <Helmet>
        <title>Online Deck Cost Estimator | Busy Beaver Construction | Calgary</title>
        <meta name="description" content="Use our free online deck cost calculator to estimate the price of your custom deck, fence, or outdoor living space in Calgary. Get instant estimates and monthly payment options." />
        <meta name="keywords" content="deck cost calculator, fence cost estimate, deck price estimator, Calgary deck pricing, composite deck cost, deck financing Calgary, monthly deck payment" />
        <link rel="canonical" href="https://busybeaverconstruction.com/estimate" />
        <meta property="og:title" content="Online Deck Cost Estimator | Busy Beaver Construction" />
        <meta property="og:description" content="Calculate the cost of your dream deck with our free online estimator. Get instant price estimates for custom decks and fences in Calgary with financing options." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://busybeaverconstruction.com/estimate" />
        <meta property="og:image" content="https://busybeaverconstruction.com/assets/images/Decks/Composite Decks/Tigerwood-and-mocha-2.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <link rel="preload" href="/assets/images/Decks/Composite Decks/Tigerwood-and-mocha-2.jpg" as="image" />
      </Helmet>
      <Header />
      <ToastContainer position="top-right" autoClose={5000} hideProgressBar={false} closeOnClick pauseOnHover />

      {/* Hero Banner */}
      <section className="relative h-[400px] flex items-center justify-center">
        <img 
          src="/assets/images/Decks/Composite Decks/Tigerwood-and-mocha-2.jpg"
          alt="TimberTech Tigerwood and Mocha composite deck installation by Busy Beaver Construction in Calgary"
          className="absolute inset-0 w-full h-full object-cover object-bottom loading-eager"
          fetchPriority="high"
        />
        <div className="relative z-10 mt-64 text-center">
          <div className="inline-block bg-busy-green rounded py-3 mx-4 px-4 lg:px-8">
            <p className="text-white text-base font-medium rounded">Calgary's Leading Custom Deck & Fence Builder</p>
          </div>
        </div>
      </section>

      {/* Estimate Content */}
      <section className="py-12 md:py-16 bg-white">
        <div className="container">
          <h1 className="text-center text-5xl lg:text-6xl font-primary mb-6">Calculate Your Deck - Online</h1>
          
          <div className="text-center mb-8">
            <p className="text-gray-700 font-text">
              Contact us now to schedule a free onsite estimate. <a href="tel:4032865400" className="text-beaver-green font-bold hover:underline">403-286-5400</a>
            </p>
          </div>
          
          {/* Estimate Calculator Form */}
          <div className="max-w-4xl mx-auto border border-gray-300 mb-10">
            <div className="bg-white p-6">
              <h2 className="text-xl font-bold text-red-600 mb-4">Calculate The Cost of Your Dream Deck!</h2>
              
              <form>
                {/* Deck Square Footage */}
                <div className="mb-4">
                  <label className={labelClass}>Deck Square Footage:</label>
                  <div className="flex">
                    <input 
                      type="number" 
                      value={squareFootage}
                      onChange={(e) => setSquareFootage(e.target.value)}
                      className={`${inputClass} mr-2`}
                      placeholder="Value"
                      required
                    />
                    <div className="w-1/4 py-2 px-3 bg-gray-100 border border-gray-300 text-gray-600 text-center">
                      SQFT
                    </div>
                  </div>
                </div>
                
                {/* Deck Material */}
                <div className="mb-4">
                  <label className={labelClass}>Deck Material:</label>
                  <select 
                    value={deckMaterial}
                    onChange={(e) => setDeckMaterial(e.target.value)}
                    className={selectClass}
                    required
                  >
                    <option value="">Select Material</option>
                    <option value="Vinyl decking">Vinyl decking</option>
                    <option value="Pressure treated wood">Pressure treated wood</option>
                    <option value="Composite">Composite</option>
                  </select>
                </div>
                
                {/* Railing */}
                <div className="mb-4">
                  <label className={labelClass}>Railing:</label>
                  <select 
                    value={hasRailing}
                    onChange={(e) => setHasRailing(e.target.value)}
                    className={selectClass}
                  >
                    <option value="No">No</option>
                    <option value="Yes">Yes</option>
                  </select>
                </div>
                
                {/* Railing Details - Conditionally displayed */}
                {hasRailing === 'Yes' && (
                  <div className="mb-4 p-3 bg-yellow-100">
                    <label className={labelClass}>Railing Linear Feet:</label>
                    <div className="flex">
                      <input 
                        type="number" 
                        value={railingFeet}
                        onChange={(e) => setRailingFeet(e.target.value)}
                        className={`${inputClass} mr-2`}
                        placeholder="Value"
                        required
                      />
                      <div className="w-1/4 py-2 px-3 bg-gray-100 border border-gray-300 text-gray-600 text-center">
                        LFT
                      </div>
                    </div>
                    
                    <label className={`${labelClass} mt-2`}>Railing Material:</label>
                    <select 
                      value={railingMaterial}
                      onChange={(e) => setRailingMaterial(e.target.value)}
                      className={selectClass}
                      required
                    >
                      <option value="">Select Material</option>
                      <option value="Glass Railing">Glass Railing</option>
                      <option value="Aluminum Picket">Aluminum Picket</option>
                      <option value="Pressure Treated Wood">Pressure Treated Wood</option>
                    </select>
                  </div>
                )}
                
                {/* Privacy Wall */}
                <div className="mb-4">
                  <label className={labelClass}>Privacy Wall (6' High):</label>
                  <select 
                    value={hasPrivacyWall}
                    onChange={(e) => setHasPrivacyWall(e.target.value)}
                    className={selectClass}
                  >
                    <option value="No">No</option>
                    <option value="Yes">Yes</option>
                  </select>
                </div>
                
                {/* Privacy Wall Feet - Conditionally displayed */}
                {hasPrivacyWall === 'Yes' && (
                  <div className="mb-4 p-3 bg-yellow-100">
                    <label className={labelClass}>Privacy Wall Linear Feet:</label>
                    <div className="flex">
                      <input 
                        type="number" 
                        value={privacyWallFeet}
                        onChange={(e) => setPrivacyWallFeet(e.target.value)}
                        className={`${inputClass} mr-2`}
                        placeholder="Value"
                      />
                      <div className="w-1/4 py-2 px-3 bg-gray-100 border border-gray-300 text-gray-600 text-center">
                        LFT
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Stairs */}
                <div className="mb-4">
                  <label className={labelClass}>Stairs (4' Wide):</label>
                  <select 
                    value={hasStairs}
                    onChange={(e) => setHasStairs(e.target.value)}
                    className={selectClass}
                  >
                    <option value="No">No</option>
                    <option value="Yes">Yes</option>
                  </select>
                </div>
                
                {/* Stairs Details - Conditionally displayed */}
                {hasStairs === 'Yes' && (
                  <div className="mb-4 p-3 bg-yellow-100">
                    <label className={labelClass}>Number of Stairs:</label>
                    <select 
                      value={stairsCount}
                      onChange={(e) => setStairsCount(e.target.value)}
                      className={selectClass}
                    >
                      <option value="">Select # of Stairs</option>
                      {[...Array(20)].map((_, i) => (
                        <option key={i+1} value={i+1}>{i+1}</option>
                      ))}
                    </select>
                    
                    <label className={`${labelClass} mt-2`}>Stairs Material:</label>
                    <select 
                      value={stairsMaterial}
                      onChange={(e) => setStairsMaterial(e.target.value)}
                      className={selectClass}
                      required
                    >
                      <option value="">Select Material</option>
                      <option value="Composite">Composite</option>
                      <option value="Pressure Treated">Pressure Treated</option>
                    </select>
                  </div>
                )}
                
                {/* Skirting */}
                <div className="mb-4">
                  <label className={labelClass}>Skirting:</label>
                  <select 
                    value={hasSkirting}
                    onChange={(e) => setHasSkirting(e.target.value)}
                    className={selectClass}
                  >
                    <option value="No">No</option>
                    <option value="Yes">Yes</option>
                  </select>
                </div>
                
                {/* Skirting Details - Conditionally displayed */}
                {hasSkirting === 'Yes' && (
                  <div className="mb-4 p-3 bg-yellow-100">
                    <label className={labelClass}>Skirting Linear Feet:</label>
                    <div className="flex">
                      <input 
                        type="number" 
                        value={skirtingFeet}
                        onChange={(e) => setSkirtingFeet(e.target.value)}
                        className={`${inputClass} mr-2`}
                        placeholder="Value"
                      />
                      <div className="w-1/4 py-2 px-3 bg-gray-100 border border-gray-300 text-gray-600 text-center">
                        LFT
                      </div>
                    </div>
                    
                    <label className={`${labelClass} mt-2`}>Skirting Material:</label>
                    <select 
                      value={skirtingMaterial}
                      onChange={(e) => setSkirtingMaterial(e.target.value)}
                      className={selectClass}
                      required
                    >
                      <option value="">Select Material</option>
                      <option value="Lattice">Lattice</option>
                      <option value="Smart Board">Smart Board</option>
                      <option value="Pressure Treated">Pressure Treated</option>
                    </select>
                  </div>
                )}
                
                {/* Fence */}
                <div className="mb-4">
                  <label className={labelClass}>Fence (6' High In Fortress Style):</label>
                  <select 
                    value={hasFence}
                    onChange={(e) => setHasFence(e.target.value)}
                    className={selectClass}
                  >
                    <option value="No">No</option>
                    <option value="Yes">Yes</option>
                  </select>
                </div>
                
                {/* Fence Details - Conditionally displayed */}
                {hasFence === 'Yes' && (
                  <div className="mb-4 p-3 bg-yellow-100">
                    <label className={labelClass}>Fence Linear Feet:</label>
                    <div className="flex">
                      <input 
                        type="number" 
                        value={fenceFeet}
                        onChange={(e) => setFenceFeet(e.target.value)}
                        className={`${inputClass} mr-2`}
                        placeholder="Value"
                      />
                      <div className="w-1/4 py-2 px-3 bg-gray-100 border border-gray-300 text-gray-600 text-center">
                        LFT
                      </div>
                    </div>
                    
                    <label className={`${labelClass} mt-2`}>Fence Type:</label>
                    <select 
                      value={fenceMaterial}
                      onChange={(e) => setFenceMaterial(e.target.value)}
                      className={selectClass}
                      required
                    >
                      <option value="">Select Fence Type</option>
                      <option value="Pressure Treated 4x4 Posts">Pressure Treated 4x4 Posts</option>
                      <option value="Pressure Treated 4x6 Posts">Pressure Treated 4x6 Posts</option>
                      <option value="Cedar 4x4 Posts">Cedar 4x4 Posts</option>
                      <option value="Cedar 4x6 Posts">Cedar 4x6 Posts</option>
                    </select>
                  </div>
                )}
                
                {/* Gate */}
                <div className="mb-4">
                  <label className={labelClass}>Gate:</label>
                  <select 
                    value={hasGate}
                    onChange={(e) => setHasGate(e.target.value)}
                    className={selectClass}
                  >
                    <option value="No">No</option>
                    <option value="Yes">Yes</option>
                  </select>
                </div>
                
                {/* Gate Count - Conditionally displayed */}
                {hasGate === 'Yes' && (
                  <div className="mb-4 p-3 bg-yellow-100">
                    <label className={labelClass}>Number of Gates:</label>
                    <select 
                      value={gateCount}
                      onChange={(e) => setGateCount(e.target.value)}
                      className={selectClass}
                    >
                      <option value="">Select # of Gates</option>
                      {[...Array(6)].map((_, i) => (
                        <option key={i+1} value={i+1}>{i+1}</option>
                      ))}
                    </select>
                  </div>
                )}
                
                {/* RV Gate */}
                <div className="mb-4">
                  <label className={labelClass}>RV Gate:</label>
                  <select 
                    value={hasRVGate}
                    onChange={(e) => setHasRVGate(e.target.value)}
                    className={selectClass}
                    required
                  >
                    <option value="No">No</option>
                    <option value="Yes">Yes</option>
                  </select>
                </div>
                
                {/* Estimated Price */}
                <div className="mb-4">
                  <label className={labelClass}>Estimated Price:</label>
                  <div className="py-2 px-3 bg-yellow-100 border border-yellow-200">
                    {estimatedPrice}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Price provided does not include GST and is only an estimate. An in-person quotation is still required and may result in prices different than provided.
                  </p>
                </div>
                
                {/* Monthly Payment */}
                <div className="mb-4">
                  <label className={labelClass}>Your low monthly payment:</label>
                  <div className="py-2 px-3 bg-yellow-100 border border-yellow-200">
                    {monthlyPayment}
                  </div>
                </div>
              </form>
            </div>
            
            {/* Claim This Estimate */}
            <div className="bg-red-600 text-white p-4">
              <h3 className="text-xl font-bold mb-1">Claim This Estimate</h3>
              <p className="text-sm mb-4">Book Your Site Visit - Get A Quote</p>
              
              {submitSuccess ? (
                <div className="bg-green-600 text-white p-4 rounded">
                  <p className="font-bold">Thank you for your submission!</p>
                  <p>We have sent you a copy of your estimate to your email. A representative will contact you shortly.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} ref={formRef}>
                  {submitError && (
                    <div className="bg-yellow-600 text-white p-3 mb-4 rounded">
                      <p>{submitError}</p>
                    </div>
                  )}
                  
                  <div className="mb-3">
                    <input 
                      type="text"
                      placeholder="First Name"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      className="w-full p-2 border border-gray-300 text-black"
                      required
                      disabled={isSubmitting}
                      name="firstName"
                    />
                  </div>
                  
                  <div className="mb-3">
                    <input 
                      type="text"
                      placeholder="Last Name"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      className="w-full p-2 border border-gray-300 text-black"
                      required
                      disabled={isSubmitting}
                      name="lastName"
                    />
                  </div>
                  
                  <div className="mb-3">
                    <input 
                      type="tel"
                      placeholder="Phone Number"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      className="w-full p-2 border border-gray-300 text-black"
                      required
                      disabled={isSubmitting}
                      name="phone"
                    />
                  </div>
                  
                  <div className="mb-3">
                    <input 
                      type="email"
                      placeholder="Email Address"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full p-2 border border-gray-300 text-black"
                      required
                      disabled={isSubmitting}
                      name="email"
                    />
                  </div>
                  
                  {/* Add hidden inputs for FormSubmit AJAX submission */}
                  <input type="hidden" name="_subject" value="New Deck Estimate Claim" />
                  <input type="hidden" name="_template" value="table" />
                  <input type="hidden" name="_captcha" value="false" />
                  <input type="hidden" name="_cc" value={email} />
                  <input type="hidden" name="squareFootage" value={squareFootage} />
                  <input type="hidden" name="deckMaterial" value={deckMaterial} />
                  <input type="hidden" name="hasRailing" value={hasRailing} />
                  <input type="hidden" name="railingFeet" value={railingFeet} />
                  <input type="hidden" name="railingMaterial" value={railingMaterial} />
                  <input type="hidden" name="hasPrivacyWall" value={hasPrivacyWall} />
                  <input type="hidden" name="privacyWallFeet" value={privacyWallFeet} />
                  <input type="hidden" name="hasStairs" value={hasStairs} />
                  <input type="hidden" name="stairsCount" value={stairsCount} />
                  <input type="hidden" name="stairsMaterial" value={stairsMaterial} />
                  <input type="hidden" name="hasSkirting" value={hasSkirting} />
                  <input type="hidden" name="skirtingFeet" value={skirtingFeet} />
                  <input type="hidden" name="skirtingMaterial" value={skirtingMaterial} />
                  <input type="hidden" name="hasFence" value={hasFence} />
                  <input type="hidden" name="fenceFeet" value={fenceFeet} />
                  <input type="hidden" name="fenceMaterial" value={fenceMaterial} />
                  <input type="hidden" name="hasGate" value={hasGate} />
                  <input type="hidden" name="gateCount" value={gateCount} />
                  <input type="hidden" name="hasRVGate" value={hasRVGate} />
                  <input type="hidden" name="estimatedPrice" value={estimatedPrice} />
                  <input type="hidden" name="monthlyPayment" value={monthlyPayment} />
                  
                  <button 
                    type="submit"
                    className="w-full bg-black text-white font-bold py-2 px-4 hover:bg-gray-800 disabled:bg-gray-500"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
                  {/* Associations and Suppliers Section - now using the component */}
                  <AssociationsAndSuppliers />

      <Footer />
    </main>
  )
} 